import type { WorkspaceTreeNode } from "../controllers/chat-workspace.ts";
import type { FleetProjectSummary } from "../types.ts";

export type FleetFileReadTarget =
  | { kind: "session"; sessionKey: string; path: string }
  | { kind: "project"; sessionKey: string; projectId: string; path: string };

export type FleetFilesTreeNode = {
  name: string;
  path: string;
  kind: "file" | "dir";
  children?: FleetFilesTreeNode[];
};

export const FLEET_FILES_ROOT = "fleet:/";
export const FLEET_FILES_PROJECTS = "fleet:/projects";
export const FLEET_FILES_SESSION = "fleet:/session-files";
export const FLEET_FILES_SUBAGENTS = "fleet:/subagents";

function joinUiPath(prefix: string, rel: string): string {
  const base = prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
  const tail = rel.trim().replace(/\\/g, "/");
  return tail ? `${base}/${tail}` : base;
}

function workspaceRelPathFromUi(uiPrefix: string, uiPath: string): string | null {
  const normalized = uiPath.replace(/\\/g, "/");
  const base = uiPrefix.replace(/\\/g, "/");
  if (!normalized.startsWith(`${base}/`)) {
    return null;
  }
  return normalized.slice(base.length + 1);
}

function mapWorkspaceBranch(
  nodes: WorkspaceTreeNode[],
  uiPrefix: string,
  targets: Record<string, FleetFileReadTarget>,
  mapFileTarget: (filePath: string) => FleetFileReadTarget,
): FleetFilesTreeNode[] {
  const out: FleetFilesTreeNode[] = [];
  for (const node of nodes) {
    if (node.kind === "dir") {
      out.push({
        name: node.name,
        path: joinUiPath(uiPrefix, node.path),
        kind: "dir",
        children: node.children?.length
          ? mapWorkspaceBranch(node.children, uiPrefix, targets, mapFileTarget)
          : [],
      });
      continue;
    }
    const uiPath = joinUiPath(uiPrefix, node.path);
    const rel = workspaceRelPathFromUi(uiPrefix, uiPath);
    if (rel) {
      targets[uiPath] = mapFileTarget(rel);
    }
    out.push({
      name: node.name,
      path: uiPath,
      kind: "file",
    });
  }
  return out;
}

export function filterSessionTreeExcludingProjectSlugs(
  nodes: WorkspaceTreeNode[],
  projectSlugs: ReadonlySet<string>,
): WorkspaceTreeNode[] {
  return nodes.filter((node) => !(node.kind === "dir" && projectSlugs.has(node.name)));
}

export function findFirstFleetFilePath(nodes: FleetFilesTreeNode[]): string | null {
  for (const node of nodes) {
    if (node.kind === "file") {
      return node.path;
    }
    if (node.children?.length) {
      const nested = findFirstFleetFilePath(node.children);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
}

export function defaultFleetFilesExpandedDirs(tree: FleetFilesTreeNode[]): Record<string, boolean> {
  const expanded: Record<string, boolean> = {
    [FLEET_FILES_ROOT]: true,
    [FLEET_FILES_PROJECTS]: true,
    [FLEET_FILES_SESSION]: true,
    [FLEET_FILES_SUBAGENTS]: true,
  };
  const walk = (nodes: FleetFilesTreeNode[]) => {
    for (const node of nodes) {
      if (node.kind === "dir") {
        if (node.path.startsWith("fleet:/projects/") || node.path.startsWith("fleet:/subagents/")) {
          expanded[node.path] = true;
        }
        if (node.children?.length) {
          walk(node.children);
        }
      }
    }
  };
  walk(tree);
  return expanded;
}

export function buildFleetSessionFilesTree(params: {
  contextSessionKey: string;
  workspaceLabel: string;
  sessionTree: WorkspaceTreeNode[];
  projects: Array<{ project: FleetProjectSummary; tree: WorkspaceTreeNode[] }>;
  subagents: Array<{ sessionKey: string; label: string; tree: WorkspaceTreeNode[] }>;
  projectSlugs: ReadonlySet<string>;
}): { tree: FleetFilesTreeNode[]; targets: Record<string, FleetFileReadTarget> } {
  const targets: Record<string, FleetFileReadTarget> = {};
  const contextKey = params.contextSessionKey.trim();

  const sessionBranch = mapWorkspaceBranch(
    filterSessionTreeExcludingProjectSlugs(params.sessionTree, params.projectSlugs),
    FLEET_FILES_SESSION,
    targets,
    (rel) => ({ kind: "session", sessionKey: contextKey, path: rel }),
  );

  const projectDirs: FleetFilesTreeNode[] = [];
  for (const { project, tree } of params.projects) {
    const uiPrefix = `fleet:/projects/${project.id}`;
    const children = mapWorkspaceBranch(tree, uiPrefix, targets, (rel) => ({
      kind: "project",
      sessionKey: contextKey,
      projectId: project.id,
      path: rel,
    }));
    projectDirs.push({
      name: project.name,
      path: uiPrefix,
      kind: "dir",
      children,
    });
  }

  const subagentDirs: FleetFilesTreeNode[] = [];
  for (const sub of params.subagents) {
    const uiPrefix = `fleet:/subagents/${encodeURIComponent(sub.sessionKey)}`;
    const children = mapWorkspaceBranch(sub.tree, uiPrefix, targets, (rel) => ({
      kind: "session",
      sessionKey: sub.sessionKey,
      path: rel,
    }));
    subagentDirs.push({
      name: sub.label,
      path: uiPrefix,
      kind: "dir",
      children,
    });
  }

  const rootChildren: FleetFilesTreeNode[] = [];
  if (projectDirs.length > 0) {
    rootChildren.push({
      name: "Projects",
      path: FLEET_FILES_PROJECTS,
      kind: "dir",
      children: projectDirs,
    });
  }
  rootChildren.push({
    name: "Chat files (no project)",
    path: FLEET_FILES_SESSION,
    kind: "dir",
    children: sessionBranch,
  });
  if (subagentDirs.length > 0) {
    rootChildren.push({
      name: "Subagents",
      path: FLEET_FILES_SUBAGENTS,
      kind: "dir",
      children: subagentDirs,
    });
  }

  return {
    tree: [
      {
        name: params.workspaceLabel,
        path: FLEET_FILES_ROOT,
        kind: "dir",
        children: rootChildren,
      },
    ],
    targets,
  };
}

function normalizeRelativePath(path: string): string {
  return path.trim().replace(/\\/g, "/").replace(/^\/+/, "");
}

/** Resolve composite fleet UI path for a repo-relative file (after workspace tree is loaded). */
export function findFleetFileUiPath(
  targets: Record<string, FleetFileReadTarget>,
  params: { relativePath: string; sessionKey?: string },
): string | null {
  const rel = normalizeRelativePath(params.relativePath);
  if (!rel) {
    return null;
  }
  const wantSession = params.sessionKey?.trim();
  const matches: Array<{ uiPath: string; priority: number }> = [];

  for (const [uiPath, target] of Object.entries(targets)) {
    if (normalizeRelativePath(target.path) !== rel) {
      continue;
    }
    if (wantSession && target.sessionKey !== wantSession) {
      continue;
    }
    let priority = 0;
    if (uiPath.startsWith(`${FLEET_FILES_SESSION}/`)) {
      priority = wantSession ? 3 : 2;
    } else if (uiPath.startsWith("fleet:/subagents/")) {
      priority = wantSession ? 4 : 1;
    } else if (uiPath.startsWith("fleet:/projects/")) {
      priority = 0;
    }
    matches.push({ uiPath, priority });
  }

  if (matches.length === 0) {
    return null;
  }
  matches.sort((a, b) => b.priority - a.priority);
  return matches[0]?.uiPath ?? null;
}

/** Parent directory paths to expand so a file row is visible in the tree. */
export function fleetUiPathAncestorDirs(uiPath: string): string[] {
  const normalized = uiPath.replace(/\\/g, "/");
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length <= 1) {
    return [FLEET_FILES_ROOT];
  }
  const dirs: string[] = [FLEET_FILES_ROOT];
  let acc = "";
  for (let i = 0; i < parts.length - 1; i += 1) {
    acc = acc ? `${acc}/${parts[i]}` : parts[i]!;
    dirs.push(acc);
  }
  return dirs;
}
